import type { MusicTrackFile } from "../assets/sounds.generated.ts";
import { resumeFrom, type TrackId } from "./audio-model.ts";

/**
 * Faixas gravadas da trilha (F84, ADR 0016): cada uma num `<audio>` em streaming (decodificar 3 min
 * em estéreo custaria ~46–69 MB) → `MediaElementAudioSourceNode` → ganho próprio → bus de música. Uma
 * por vez, com crossfade; a mesma faixa em outro estado segue tocando. A que sai pausa no fim do
 * fade e, pedida de novo em até 60 s, volta de onde parou. Só MP3: o WebKit toca WebM no `<audio>`,
 * mas a fonte dele no contexto sai muda. Um erro no arquivo marca a faixa como falha (o motor cai na
 * partitura). Nada aqui lança exceção.
 */
export interface MediaLike {
  src: string;
  preload: string;
  loop: boolean;
  currentTime: number;
  readonly paused: boolean;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  removeAttribute(name: string): void;
  addEventListener(type: "error", fn: () => void): void;
}

export interface MusicDeps {
  create(): MediaLike | undefined;
  /** A fonte do elemento no contexto (antes do primeiro `play`: senão ele toca direto na saída). */
  source(ctx: BaseAudioContext, el: MediaLike): AudioNode | undefined;
  now(): number;
}

export const browserDeps = (): MusicDeps => ({
  create: () => (typeof Audio !== "undefined" ? new Audio() : undefined),
  source: (ctx, el) => (ctx as AudioContext).createMediaElementSource(el as HTMLMediaElement),
  now: () => performance.now(),
});

export interface MusicStats {
  id: TrackId | undefined;
  playing: boolean;
  /** Posição da faixa atual (s). */
  time: number;
  /** O navegador recusou o `play` da faixa atual sem gesto (WebKit): tenta de novo no próximo. */
  blocked: boolean;
  failed: TrackId[];
  /** RMS da saída das faixas (dBFS), para o e2e saber que soa de fato. */
  level: number;
}

interface Slot {
  el: MediaLike;
  gain: GainNode;
  level: number;
  /** Pausa agendada para o fim do fade de saída. */
  pausing: ReturnType<typeof setTimeout> | undefined;
  position: number;
  pausedAt: number | undefined;
  /** O último `play` foi recusado sem gesto. */
  blocked: boolean;
}

/** Entrada da faixa que só toca no gesto seguinte (a rampa dela já acabou enquanto esperava). */
const PRIMED_FADE_IN = 2;

export class MusicPlayer {
  /** O motor cai na partitura quando a faixa do estado falha. */
  onFail: ((id: TrackId) => void) | undefined;
  private ctx: BaseAudioContext | undefined;
  private analyser: AnalyserNode | undefined;
  private out: AudioNode | undefined;
  private readonly slots = new Map<TrackId, Slot>();
  private readonly failed = new Set<TrackId>();
  private current: TrackId | undefined;
  private hidden = false;
  private readonly tracks: Readonly<Record<TrackId, MusicTrackFile>>;
  private readonly deps: MusicDeps;

  constructor(tracks: Readonly<Record<TrackId, MusicTrackFile>>, deps: MusicDeps = browserDeps()) {
    this.tracks = tracks;
    this.deps = deps;
  }

  /** Contexto criado (primeiro gesto): as faixas saem por um analisador para `out`. */
  attach(ctx: BaseAudioContext, out: AudioNode): void {
    this.ctx = ctx;
    this.out = out;
    try {
      const a = ctx.createAnalyser();
      a.fftSize = 256;
      a.connect(out);
      this.analyser = a;
    } catch {
      this.analyser = undefined;
    }
  }

  /**
   * Pede a faixa (`undefined`: nenhuma). Devolve se a faixa vai tocar; falso quando não há faixa ou
   * ela falhou (o motor toca a partitura). Sem contexto ainda, só responde: `attach` e o motor pedem
   * de novo.
   */
  set(id: TrackId | undefined, fade: { out: number; in: number }): boolean {
    const wanted = id !== undefined && !this.failed.has(id) ? id : undefined;
    if (!this.ctx) return wanted !== undefined;
    if (wanted !== undefined && wanted === this.current) return true;
    if (this.current !== undefined) this.release(this.current, fade.out);
    this.current = undefined;
    if (wanted === undefined) return false;
    const slot = this.slots.get(wanted) ?? this.create(wanted);
    if (!slot) {
      this.failed.add(wanted);
      return false;
    }
    this.current = wanted;
    if (slot.pausing !== undefined) {
      // Voltou antes da pausa: segue de onde está, só sobe de novo (e volta a tocar se a aba oculta
      // ou um `play` recusado a deixaram parada).
      clearTimeout(slot.pausing);
      slot.pausing = undefined;
      if (slot.el.paused) this.play(slot);
    } else {
      try {
        slot.el.currentTime = resumeFrom(slot.position, slot.pausedAt, this.deps.now());
      } catch {
        // sem metadados ainda: começa do início
      }
      this.play(slot);
    }
    this.ramp(slot, slot.level, fade.in);
    return true;
  }

  /** Dentro de um gesto: a faixa atual parada (`play` recusado) toca, subindo do zero. */
  prime(): void {
    const slot = this.current !== undefined ? this.slots.get(this.current) : undefined;
    if (!slot || !slot.el.paused || this.hidden) return;
    if (slot.blocked) this.ramp(slot, slot.level, PRIMED_FADE_IN, 0);
    this.play(slot);
  }

  /** Aba oculta: pausa tudo (o contexto suspenso não para o elemento); de volta, só a atual. */
  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    if (hidden) for (const s of this.slots.values()) this.pause(s);
    else if (this.current !== undefined) {
      const slot = this.slots.get(this.current);
      if (slot) this.play(slot);
    }
  }

  stats(): MusicStats {
    const slot = this.current !== undefined ? this.slots.get(this.current) : undefined;
    return {
      id: this.current,
      playing: !!slot && !slot.el.paused,
      time: slot?.el.currentTime ?? 0,
      blocked: slot?.blocked ?? false,
      failed: [...this.failed],
      level: this.level(),
    };
  }

  dispose(): void {
    for (const s of this.slots.values()) {
      if (s.pausing !== undefined) clearTimeout(s.pausing);
      try {
        s.el.pause();
        s.el.removeAttribute("src");
        s.el.load();
        s.gain.disconnect();
      } catch {
        // já solto
      }
    }
    this.slots.clear();
    this.failed.clear();
    this.current = undefined;
    try {
      this.analyser?.disconnect();
    } catch {
      // já solto
    }
    this.analyser = undefined;
    this.ctx = undefined;
  }

  private create(id: TrackId): Slot | undefined {
    const ctx = this.ctx;
    const out = this.analyser ?? this.out;
    if (!ctx || !out) return undefined;
    try {
      const el = this.deps.create();
      if (!el) return undefined;
      const node = this.deps.source(ctx, el);
      if (!node) return undefined;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      node.connect(gain).connect(out);
      el.preload = "auto";
      el.loop = true;
      el.src = this.tracks[id].mp3;
      const slot: Slot = { el, gain, level: this.tracks[id].gain, pausing: undefined, position: 0, pausedAt: undefined, blocked: false };
      el.addEventListener("error", () => this.fail(id, slot));
      this.slots.set(id, slot);
      return slot;
    } catch {
      return undefined;
    }
  }

  /** Arquivo que não toca (404, decodificação): a faixa falhou. */
  private fail(id: TrackId, slot: Slot): void {
    this.failed.add(id);
    this.pause(slot);
    if (this.current !== id) return;
    this.current = undefined;
    this.onFail?.(id);
  }

  /** A faixa que sai: desce até o zero no fade e pausa no fim dele, guardando a posição. */
  private release(id: TrackId, out: number): void {
    const slot = this.slots.get(id);
    if (!slot) return;
    this.ramp(slot, 0, out);
    if (slot.pausing !== undefined) clearTimeout(slot.pausing);
    slot.pausing = setTimeout(
      () => {
        slot.pausing = undefined;
        slot.position = slot.el.currentTime;
        slot.pausedAt = this.deps.now();
        this.pause(slot);
      },
      (out + 0.1) * 1000,
    );
  }

  /** Rampa linear até `to` em `seconds`, do valor de agora (a anterior pode estar no meio) ou de `from`. */
  private ramp(slot: Slot, to: number, seconds: number, from?: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      // Linear chega ao zero de fato no fim do fade (a exponencial ficaria em −27 dB na pausa).
      const t = ctx.currentTime;
      const g = slot.gain.gain;
      const v = from ?? g.value;
      g.cancelScheduledValues(t);
      g.setValueAtTime(v, t);
      g.linearRampToValueAtTime(to, t + Math.max(0.01, seconds));
    } catch {
      // sem automação: fica no valor
    }
  }

  private play(slot: Slot): void {
    if (this.hidden) return;
    try {
      void slot.el.play().then(
        () => (slot.blocked = false),
        (e: unknown) => {
          if ((e as { name?: string } | undefined)?.name === "NotAllowedError") slot.blocked = true;
        },
      );
    } catch {
      // sem mídia: o erro chega pelo evento
    }
  }

  private pause(slot: Slot): void {
    try {
      slot.el.pause();
    } catch {
      // já parado
    }
  }

  private level(): number {
    const a = this.analyser;
    if (!a) return -Infinity;
    try {
      const data = new Float32Array(a.fftSize);
      a.getFloatTimeDomainData(data);
      let acc = 0;
      for (const x of data) acc += x * x;
      return acc > 0 ? 10 * Math.log10(acc / data.length) : -Infinity;
    } catch {
      return -Infinity;
    }
  }
}
